import { useEffect, useState } from 'react'
import { Layer, Source, useMap } from 'react-map-gl'
import type { MapLayerDataset, MapLayerVariant } from './catalogue'
import { layerPaint } from './catalogue'

function flowArrow() {
  const width = 24
  const data = new Uint8Array(width * width * 4)
  for (let y = 0; y < width; y++)
    for (let x = 0; x < width; x++) {
      if ((x >= 3 && x <= 16 && Math.abs(y - 12) <= 2) || (x >= 12 && x <= 21 && Math.abs(y - 12) <= 21 - x)) {
        data.set([51, 65, 85, 255], (y * width + x) * 4)
      }
    }
  return { width, height: width, data }
}

export default function MapLayerRenderer({
  selections,
  opacity,
}: {
  selections: { dataset: MapLayerDataset; variant: MapLayerVariant }[]
  opacity: number
}) {
  const { current } = useMap()
  const [beforeId, setBeforeId] = useState<string>()
  useEffect(() => {
    const map = current?.getMap()
    if (!map) return
    const refresh = () => {
      if (!map.isStyleLoaded()) return
      if (!map.hasImage('climate-flow-arrow')) map.addImage('climate-flow-arrow', flowArrow())
      const target = map
        .getStyle()
        ?.layers?.find(
          layer =>
            !layer.id.startsWith('climate-') &&
            (layer.type === 'symbol' ||
              ('source' in layer &&
                typeof layer.source === 'string' &&
                map.getSource(layer.source)?.type === 'geojson')),
        )
      setBeforeId(previous => (previous === target?.id ? previous : target?.id))
    }
    const missingImage = (event: { id: string }) => {
      if (event.id === 'climate-flow-arrow' && !map.hasImage(event.id)) map.addImage(event.id, flowArrow())
    }
    refresh()
    map.on('styledata', refresh)
    map.on('load', refresh)
    map.on('idle', refresh)
    map.on('styleimagemissing', missingImage)
    return () => {
      map.off('styledata', refresh)
      map.off('load', refresh)
      map.off('idle', refresh)
      map.off('styleimagemissing', missingImage)
    }
  }, [current])

  return (
    <>
      {selections.flatMap(({ dataset, variant }) =>
        variant.render.sources.map(source => {
          const { id, layers, ...props } = source
          const sourceId = `climate-${dataset.id}-${variant.id}-${id}`
          return (
            <Source key={sourceId} {...props} id={sourceId}>
              {layers.map((layer, index) => (
                <Layer
                  {...layer}
                  key={`${sourceId}-${index}`}
                  id={`${sourceId}-${index}`}
                  source={sourceId}
                  beforeId={beforeId && current?.getLayer(beforeId) ? beforeId : undefined}
                  paint={layerPaint(layer, opacity) as any}
                />
              ))}
            </Source>
          )
        }),
      )}
    </>
  )
}
